import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Info, Shield, AlertTriangle, CheckCircle, Clock, Smartphone, Download, Copy, Eye, EyeOff, Key, Phone, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useLoginActivity } from '@/hooks/useLoginActivity';
import { LoginActivityModal } from '@/components/settings/LoginActivityModal';
import { geolocationService } from '@/services/geolocationService';

interface SecurityTabProps {
  twoFactorEnabled: boolean;
  setTwoFactorEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ModernSecurityTab = ({ twoFactorEnabled, setTwoFactorEnabled }: SecurityTabProps) => {
  const { toast } = useToast();
  const [showQRCode, setShowQRCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Recovery options state
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [phoneVerificationStep, setPhoneVerificationStep] = useState<'input' | 'verify'>('input');
  const [recoveryPhone, setRecoveryPhone] = useState<string | null>(null);
  
  // Backup codes state
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showCodes, setShowCodes] = useState(false);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [hasBackupCodes, setHasBackupCodes] = useState(false);
  
  // Login activity state
  const [showAllActivityModal, setShowAllActivityModal] = useState(false);
  
  // Location tracking state
  const [locationTrackingEnabled, setLocationTrackingEnabled] = useState(true);
  const [clearingLocationCache, setClearingLocationCache] = useState(false);
  
  // Use login activity hook for real-time data
  const { 
    recentActivity, 
    allActivity, 
    isLoading: activityLoading, 
    error: activityError,
    markCurrentSession 
  } = useLoginActivity();

  // Generate cryptographically secure backup codes
  const generateBackupCodes = () => {
    const codes = [];
    for (let i = 0; i < 10; i++) {
      // Generate 8-character alphanumeric codes
      const code = Array.from({ length: 8 }, () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        return chars.charAt(Math.floor(Math.random() * chars.length));
      }).join('');
      codes.push(code);
    }
    return codes;
  };

  // Location tracking handlers
  const handleLocationTrackingToggle = async (enabled: boolean) => {
    setLocationTrackingEnabled(enabled);
    
    // Save preference to localStorage
    localStorage.setItem('location-tracking-enabled', enabled.toString());
    
    if (!enabled) {
      // Clear location cache when disabled
      geolocationService.clearCache();
      toast({
        title: 'Location Tracking Disabled',
        description: 'Location information will no longer be collected for login activity.',
      });
    } else {
      toast({
        title: 'Location Tracking Enabled',
        description: 'Location information will be collected to enhance security monitoring.',
      });
    }
  };

  const handleClearLocationCache = async () => {
    setClearingLocationCache(true);
    
    try {
      // Clear geolocation cache
      geolocationService.clearCache();
      
      // Clear any stored location data
      localStorage.removeItem('cached-location');
      
      toast({
        title: 'Location Cache Cleared',
        description: 'All cached location data has been removed.',
      });
    } catch (error) {
      console.error('Error clearing location cache:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear location cache.',
        variant: 'destructive',
      });
    } finally {
      setClearingLocationCache(false);
    }
  };

  // Load location tracking preference on component mount
  React.useEffect(() => {
    const savedPreference = localStorage.getItem('location-tracking-enabled');
    if (savedPreference !== null) {
      setLocationTrackingEnabled(savedPreference === 'true');
    }
  }, []);

  // Handle phone number configuration
  const handlePhoneConfiguration = async () => {
    if (phoneVerificationStep === 'input') {
      if (!phoneNumber || phoneNumber.length < 10) {
        toast({
          title: 'Invalid Phone Number',
          description: 'Please enter a valid phone number.',
          variant: 'destructive',
        });
        return;
      }

      setVerifyingPhone(true);
      
      // Simulate sending verification code
      setTimeout(() => {
        setVerifyingPhone(false);
        setPhoneVerificationStep('verify');
        toast({
          title: 'Verification Code Sent',
          description: `A 6-digit code has been sent to ${phoneNumber}`,
        });
      }, 1500);
    } else {
      // Verify the code
      if (phoneVerificationCode.length !== 6) {
        toast({
          title: 'Invalid Code',
          description: 'Please enter the 6-digit verification code.',
          variant: 'destructive',
        });
        return;
      }

      setVerifyingPhone(true);
      
      // Simulate verification
      setTimeout(async () => {
        try {
          // Store in Supabase (mock for now)
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // In a real implementation, you'd store this in user metadata
            console.log('Recovery phone saved for user:', user.id, phoneNumber);
          }
          
          setRecoveryPhone(phoneNumber);
          setVerifyingPhone(false);
          setShowPhoneDialog(false);
          setPhoneVerificationStep('input');
          setPhoneNumber('');
          setPhoneVerificationCode('');
          
          toast({
            title: 'Recovery Phone Added',
            description: 'Your recovery phone number has been successfully configured.',
          });
        } catch (error) {
          setVerifyingPhone(false);
          toast({
            title: 'Configuration Failed',
            description: 'Failed to save recovery phone. Please try again.',
            variant: 'destructive',
          });
        }
      }, 1000);
    }
  };

  // Handle backup codes generation
  const handleGenerateBackupCodes = async () => {
    setGeneratingCodes(true);
    
    try {
      const codes = generateBackupCodes();
      
      // Simulate saving to backend
      setTimeout(async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // In a real implementation, you'd hash and store these codes
            console.log('Backup codes generated for user:', user.id, codes);
          }
          
          setBackupCodes(codes);
          setHasBackupCodes(true);
          setGeneratingCodes(false);
          setShowBackupCodesDialog(true);
          
          toast({
            title: 'Backup Codes Generated',
            description: 'Your recovery codes have been generated. Save them in a secure location.',
          });
        } catch (error) {
          setGeneratingCodes(false);
          toast({
            title: 'Generation Failed',
            description: 'Failed to generate backup codes. Please try again.',
            variant: 'destructive',
          });
        }
      }, 1500);
    } catch (error) {
      setGeneratingCodes(false);
      toast({
        title: 'Error',
        description: 'An error occurred while generating backup codes.',
        variant: 'destructive',
      });
    }
  };

  // Copy codes to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: 'Copied to Clipboard',
        description: 'Backup codes have been copied to your clipboard.',
      });
    });
  };

  // Download codes as text file
  const downloadCodes = () => {
    const codesText = `Stewie Trader Recovery Codes\n\nGenerated: ${new Date().toLocaleString()}\n\nIMPORTANT: Store these codes in a secure location. Each code can only be used once.\n\n${backupCodes.map((code, index) => `${index + 1}. ${code}`).join('\n')}\n\nIf you lose access to your account, you can use any of these codes to regain access.\nAfter using a code, it will no longer work.`;
    
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stewie-trader-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Codes Downloaded',
      description: 'Your backup codes have been downloaded as a text file.',
    });
  };
  
  // Function to enable or verify 2FA
  const handleTwoFactor = () => {
    if (!twoFactorEnabled) {
      // Enable 2FA flow
      setShowQRCode(true);
    } else {
      // Disable 2FA
      setIsSubmitting(true);
      
      // Simulate API call
      setTimeout(() => {
        setTwoFactorEnabled(false);
        setIsSubmitting(false);
        setShowQRCode(false);
        
        toast({
          title: 'Two-Factor Authentication Disabled',
          description: 'Your account is now less secure. We recommend enabling 2FA for maximum security.',
          variant: 'destructive',
        });
      }, 1000);
    }
  };
  
  // Function to verify 2FA code
  const verifyTwoFactorCode = () => {
    setIsSubmitting(true);
    
    // Simulate API call with validation
    setTimeout(() => {
      if (verificationCode.length === 6) {
        setTwoFactorEnabled(true);
        setShowQRCode(false);
        setVerificationCode('');
        
        toast({
          title: 'Two-Factor Authentication Enabled',
          description: 'Your account is now more secure.',
          variant: 'success',
        });
      } else {
        toast({
          title: 'Invalid Code',
          description: 'Please enter a valid 6-digit code.',
          variant: 'destructive',
        });
      }
      
      setIsSubmitting(false);
    }, 1000);
  };
  
  return (
    <div className="p-0">
      <CardHeader className="pb-3">
        <CardTitle className="text-slate-100">Security Settings</CardTitle>
        <CardDescription className="text-slate-400">
          Manage your account security and privacy preferences
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        <div className="space-y-6">
          {/* Two-Factor Authentication */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2 text-slate-200">Two-Factor Authentication</h3>
              <p className="text-sm text-slate-400 mb-4">
                Add an extra layer of security to your account by requiring a verification code 
                in addition to your password when you sign in.
              </p>
            </div>
            
            <div className="rounded-md border p-4 border-slate-700">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-100">Authenticator App</div>
                    <p className="text-sm text-slate-400 mt-1">
                      Use an authenticator app to generate verification codes when prompted.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {twoFactorEnabled && (
                    <Badge variant="outline" className="bg-green-500/20 text-green-300 border-green-500/30">
                      Enabled
                    </Badge>
                  )}
                  <Button 
                    variant={twoFactorEnabled ? "destructive" : "default"}
                    size="sm"
                    onClick={handleTwoFactor}
                    disabled={isSubmitting}
                    className="text-slate-50"
                  >
                    {twoFactorEnabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>
              </div>
              
              {showQRCode && (
                <div className="mt-4 p-4 border rounded-md bg-slate-700/30 border-slate-600">
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    <div className="rounded-md bg-white p-4 w-40 h-40 flex-shrink-0">
                      {/* Placeholder for QR code - in a real app, this would be a dynamic QR code */}
                      <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDE2MCAxNjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTAgMEgxNlYxNkgwVjBaTTE4IDBIMTRWMTRIMTJWMTJIOFY4SDBWNkgyVjJINFYwSDE4Wk0yIDJINlY2SDJWMlpNNCAxNkgwVjEySDBWMTBINFYxNlpNNiAySDhWNEg2VjJaTTQgNEg2VjZINFY0Wk0xMCA2SDhWNEgxMFY2Wk0xNCAxMkgxMlYxMEgxMFY4SDEyVjYHhwVjEySDE2VjE2SDE0VjEyWk0xNiA4SDE4VjEwSDE2VjhaTTE0IDJIMTZWNEgxNFYyWk0xNiA0SDE4VjZIMTZWNFpNMTAgMTBIMTJWMTJIMTBWMTBaTTQgOEg2VjEwSDRWOFoiIGZpbGw9ImJsYWNrIi8+PC9zdmc+')] bg-center bg-no-repeat"></div>
                    </div>
                    <div className="space-y-4 flex-1">
                      <div>
                        <h4 className="text-sm font-medium text-slate-200">Set up authenticator app</h4>
                        <ol className="text-sm text-slate-400 mt-1 space-y-2 list-decimal pl-4">
                          <li>Install an authenticator app (like Google Authenticator, Authy, etc.)</li>
                          <li>Scan the QR code with the app</li>
                          <li>Enter the 6-digit verification code from the app</li>
                        </ol>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="verification-code" className="text-slate-300">Verification Code</Label>
                        <div className="flex gap-2">
                          <Input
                            id="verification-code"
                            placeholder="000000"
                            maxLength={6}
                            className="flex-1 bg-slate-700 border-slate-600 text-slate-50 placeholder-slate-400"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                          />
                          <Button 
                            onClick={verifyTwoFactorCode}
                            disabled={verificationCode.length !== 6 || isSubmitting}
                            className="bg-blue-600 hover:bg-blue-700 text-slate-50"
                          >
                            {isSubmitting ? 'Verifying...' : 'Verify'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <Separator className="bg-slate-700"/>
          
          {/* Security Recovery */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2 text-slate-200">Recovery Options</h3>
              <p className="text-sm text-slate-400 mb-4">
                Configure backup methods to recover your account in case you lose access to your primary authentication method.
              </p>
            </div>
            
            <div className="space-y-3">
              <div className="rounded-md border p-4 border-slate-700">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-5 w-5 text-slate-400" />
                    <div>
                      <Label className="font-medium cursor-pointer text-slate-300">Recovery Phone Number</Label>
                      <p className="text-sm text-slate-400 mt-1">
                        {recoveryPhone ? `Configured: ${recoveryPhone}` : 'Add a phone number for account recovery via SMS'}
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => setShowPhoneDialog(true)}
                    className="bg-slate-700 text-white border border-slate-600 hover:bg-slate-600 hover:border-slate-500"
                  >
                    {recoveryPhone ? 'Update' : 'Configure'}
                  </Button>
                </div>
              </div>
              
              <div className="rounded-md border p-4 border-slate-700">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Key className="h-5 w-5 text-slate-400" />
                    <div>
                      <Label className="font-medium cursor-pointer text-slate-300">Recovery Backup Codes</Label>
                      <p className="text-sm text-slate-400 mt-1">
                        {hasBackupCodes ? 'You have active backup codes' : 'Generate one-time use backup codes for account recovery'}
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={handleGenerateBackupCodes}
                    disabled={generatingCodes}
                    className="bg-slate-700 text-white border border-slate-600 hover:bg-slate-600 hover:border-slate-500 disabled:opacity-50"
                  >
                    {generatingCodes ? 'Generating...' : hasBackupCodes ? 'Regenerate' : 'Generate Codes'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Recovery Best Practices */}
            <div className="rounded-md bg-blue-950/20 border border-blue-800/30 p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-200">
                    Recovery Best Practices
                  </p>
                  <ul className="text-xs text-blue-300 space-y-1">
                    <li>• Store backup codes in a secure location (password manager, safe, etc.)</li>
                    <li>• Keep your recovery phone number updated</li>
                    <li>• Each backup code can only be used once</li>
                    <li>• Generate new codes if you suspect they've been compromised</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <Separator className="bg-slate-700"/>
          
          {/* Location Tracking */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2 text-slate-200">Location Tracking</h3>
              <p className="text-sm text-slate-400 mb-4">
                Control how location information is collected and used for security monitoring.
              </p>
            </div>
            
            <div className="space-y-3">
              <div className="rounded-md border p-4 border-slate-700">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-700 rounded-md">
                      <AlertCircle className="h-5 w-5 text-orange-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-200">Location Tracking</h4>
                      <p className="text-sm text-slate-400">
                        {locationTrackingEnabled 
                          ? 'Location information is collected to enhance security monitoring'
                          : 'Location information is not being collected'
                        }
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={locationTrackingEnabled}
                    onCheckedChange={handleLocationTrackingToggle}
                    className="data-[state=checked]:bg-blue-600"
                  />
                </div>
                
                {locationTrackingEnabled && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-sm font-medium text-slate-300">Location Cache</h5>
                        <p className="text-xs text-slate-500">
                          Clear cached location data for enhanced privacy
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleClearLocationCache}
                        disabled={clearingLocationCache}
                        className="text-xs bg-slate-700 border-slate-600 hover:bg-slate-600"
                      >
                        {clearingLocationCache ? 'Clearing...' : 'Clear Cache'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-slate-300 font-medium">How location tracking works</p>
                    <p className="text-xs text-slate-400 mt-1">
                      When enabled, we use your device's location (if permitted) or IP address to determine your approximate location. 
                      This helps us detect unusual login patterns and enhance account security.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <Separator className="bg-slate-700"/>
          
          {/* Login History */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2 text-slate-200">Recent Login Activity</h3>
              <p className="text-sm text-slate-400 mb-4">
                Review your recent sign-in history. If you don't recognize an activity, change your password immediately.
              </p>
            </div>
            
            <div className="rounded-md border border-slate-700">
              <div className="grid grid-cols-12 bg-slate-700/50 py-2.5 px-4 text-xs font-medium text-slate-300">
                <div className="col-span-4">Date & Time</div>
                <div className="col-span-3">Device</div>
                <div className="col-span-3">Location</div>
                <div className="col-span-2">Status</div>
              </div>
              
              {activityLoading ? (
                <div className="p-8 text-center text-slate-400">
                  Loading activity...
                </div>
              ) : activityError ? (
                <div className="p-8 text-center text-red-400">
                  {activityError}
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  No recent login activity
                </div>
              ) : (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="grid grid-cols-12 py-2.5 px-4 text-sm border-t border-slate-700 text-slate-200">
                    <div className="col-span-4 text-slate-400 text-xs flex items-center">
                      <Clock className="h-3.5 w-3.5 mr-1.5 inline text-slate-500" />
                      {activity.date}
                    </div>
                    <div className="col-span-3">{activity.device}</div>
                    <div className="col-span-3">{activity.location}</div>
                    <div className="col-span-2">
                      {activity.status === 'Current session' && (
                        <Badge variant="outline" className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-xs">
                          Current
                        </Badge>
                      )}
                      {activity.status === 'Success' && (
                        <span className="flex items-center text-green-400 text-xs">
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          Success
                        </span>
                      )}
                      {activity.status === 'Blocked' && (
                        <span className="flex items-center text-red-400 text-xs">
                          <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                          Blocked
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="text-right">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAllActivityModal(true)}
                className="text-xs text-blue-400 hover:bg-slate-700"
              >
                View All Activity ({allActivity.length})
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Phone Configuration Dialog */}
      <Dialog open={showPhoneDialog} onOpenChange={setShowPhoneDialog}>
        <DialogContent className="sm:max-w-[425px] bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-100">
              <Phone className="h-5 w-5 text-blue-500" />
              {phoneVerificationStep === 'input' ? 'Configure Recovery Phone' : 'Verify Phone Number'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {phoneVerificationStep === 'input' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-300">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-slate-50"
                  />
                  <p className="text-xs text-slate-400">
                    We'll send a verification code to this number
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="phone-code" className="text-slate-300">Verification Code</Label>
                  <Input
                    id="phone-code"
                    placeholder="123456"
                    maxLength={6}
                    value={phoneVerificationCode}
                    onChange={(e) => setPhoneVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                    className="bg-slate-700 border-slate-600 text-slate-50"
                  />
                  <p className="text-xs text-slate-400">
                    Enter the 6-digit code sent to {phoneNumber}
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPhoneDialog(false);
                setPhoneVerificationStep('input');
                setPhoneNumber('');
                setPhoneVerificationCode('');
              }}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePhoneConfiguration}
              disabled={verifyingPhone}
              className="bg-blue-600 hover:bg-blue-700 text-slate-50"
            >
              {verifyingPhone ? 'Processing...' : phoneVerificationStep === 'input' ? 'Send Code' : 'Verify'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodesDialog} onOpenChange={setShowBackupCodesDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-100">
              <Key className="h-5 w-5 text-blue-500" />
              Recovery Backup Codes
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-950/20 border border-amber-800/30 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-200">
                    Important: Save These Codes
                  </p>
                  <p className="text-xs text-amber-300">
                    Store these codes in a secure location. Each code can only be used once. 
                    If you lose access to your authenticator, you can use these codes to regain access.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Your Backup Codes</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCodes(!showCodes)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    {showCodes ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(backupCodes.join('\n'))}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadCodes}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <div 
                    key={index}
                    className="p-3 rounded border border-slate-700 bg-slate-800/30 font-mono text-sm text-slate-200"
                  >
                    {showCodes ? code : '••••••••'}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={() => setShowBackupCodesDialog(false)}
              className="dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-slate-50"
            >
              I've Saved These Codes
            </Button>
                     </DialogFooter>
         </DialogContent>
       </Dialog>

       {/* Login Activity Modal */}
       <LoginActivityModal
         isOpen={showAllActivityModal}
         onClose={() => setShowAllActivityModal(false)}
         activities={allActivity}
         isLoading={activityLoading}
       />
     </div>
   );
 };